import {
  ArrowLeftOutlined,
  ArrowRightOutlined
} from "@ant-design/icons";
import { Card, Empty, Spin } from "antd";
import SaudiRiyal from "assets/svg/saudi_riyal_symbol.svg";
import useAuthHook from "hooks/useAuthHook";
import { useEffect, useMemo, useState } from "react";
import Chart from 'react-apexcharts';
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useGetInstlmntDashboardMutation
} from "services/auth";
import routes, { appRoute } from "shared/constants/routes";
import formatDateDDMMYYYY from "utilities/formatDate";
import { useTranslation } from "../../../contexts/TranslationContext";
import contractedAmount from "../../../assets/svg/contracted_Amount.svg"
import remainingAmount from "../../../assets/svg/remaining_amount.svg"
import paidAmountImg from "../../../assets/svg/paid_Amount.svg"
import nextPayment from "../../../assets/svg/next_payment.svg"
import RepaymentCard from "components/repayment/RepaymentCard";
import { ApexChart } from "components/repayment/TrendChart";
import { CalendarView } from "components/repayment/Calender";
import { StatusChart } from "components/repayment/StatusChart";


const InstlmntDashboard = () => {
  const { translate, language } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Application picked in the Settings "Installment details" selection modal
  // (V2 flow only — see Setting.jsx). Falls back to the legacy behavior below
  // when absent (V1, or direct navigation without going through the modal).
  const selectedApplId = location.state?.applId;
  const { getUser } = useAuthHook();
  const isRtl = language.dir === "rtl";
  const [getInstlmntDashboardData] = useGetInstlmntDashboardMutation();
  const [installmentData, setInstallmentData] = useState([]);
  const [paidAmount, setPaidAmount] = useState(0);
  const [unpaidAmount, setUnpaidAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [appResults, setAppResults] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [nextPaymentDate, setNextPaymentDate] = useState(null);
  const [upcomingAmount, setUpcomingAmount] = useState(0);
  const [visibleInstallments, setVisibleInstallments] = useState(7);

  const navigateBack = () => {
    if (appResults.length > 1 && selectedAppId) {
      setSelectedAppId(null);
    } else {
      navigate(appRoute(routes.SETTING_ROOT));
    }
  };

  const handleShowMore = () => {
    setVisibleInstallments(prev => prev + 7);
  };

  // Memoized chart data updates when amounts change
  const chartData = useMemo(() => ({
    series: [paidAmount, unpaidAmount],
    options: {
      chart: {
        type: 'donut',
        height: 220,
      },
      labels: ['Paid', 'Pending'],
      colors: ['#22C55E', '#EF4444'],
      legend: {
        show: false
      },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '16px',
                fontWeight: 600,
                color: '#374151',
                offsetY: -10,
                formatter: () => 'Total'
              },
              value: {
                show: true,
                fontSize: '24px',
                fontWeight: 700,
                color: '#111827',
                offsetY: 10,
                formatter: () => totalAmount.toLocaleString()
              },
              total: {
                show: true,
                showAlways: true,
                label: 'Total',
                fontSize: '16px',
                fontWeight: 600,
                color: '#374151',
                formatter: () => totalAmount.toLocaleString()
              }
            }
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            width: 350,
            height: 250
          }
        }
      }]
    }
  }), [paidAmount, unpaidAmount, totalAmount]);

  // extract chart & table data for one app (v1)
  const processAppData = (app) => {
    const formatted = app.result.map(item => ({
      installmentNumber: item.INSTLMNT_NO,
      dueDate: formatDateDDMMYYYY(item.DUE_DATE),
      installmentAmount: item.INSTLMNT_AMT,
      status: item.PAID_FLAG === 'Y' ? 'Paid' : 'Pending',
      rawDate: new Date(item.DUE_DATE)
    }));

    const total = app.totalAmount || 0;
    const paid = app.paidAmount || 0;
    const unpaid = app.unpaidAmount || 0;

    const upcomingInstallments = formatted
      .filter(item => item.status === 'Pending' && item.rawDate >= new Date())
      .sort((a, b) => a.rawDate - b.rawDate);

    const nextPayment = upcomingInstallments[0];
    setNextPaymentDate(nextPayment ? nextPayment.rawDate : null);

    const upcoming = upcomingInstallments.reduce((sum, item) => sum + item.installmentAmount, 0);
    setUpcomingAmount(upcoming);

    setInstallmentData(formatted);
    setPaidAmount(paid);
    setUnpaidAmount(unpaid);
    setTotalAmount(total);
  };

  // extract chart & table data from v2 Temenos response
  const processAppDataV2 = (data) => {
    const parseAmount = (str) => parseFloat(String(str || '0').replace(/,/g, '')) || 0;

    const formatted = (data.body || []).map(item => ({
      installmentNumber: Number(item.SerialNo),
      dueDate: formatDateDDMMYYYY(item.paymentDate),
      installmentAmount: parseFloat(item.emiAmount) || 0,
      status: item.Status?.toUpperCase() === 'PAID' ? 'Paid' : 'Pending',
      rawDate: new Date(item.paymentDate),
    }));

    const total = parseAmount(data.header?.data?.disbursementAmount);
    const paid = parseAmount(data.header?.data?.principalPaid);
    const unpaid = parseAmount(data.header?.data?.outstandingPrincipal);

    const upcomingInstallments = formatted
      .filter(item => item.status === 'Pending' && item.rawDate >= new Date())
      .sort((a, b) => a.rawDate - b.rawDate);

    const nextPayment = upcomingInstallments[0];
    setNextPaymentDate(nextPayment ? nextPayment.rawDate : null);

    const upcoming = upcomingInstallments.reduce((sum, item) => sum + item.installmentAmount, 0);
    setUpcomingAmount(upcoming);

    setInstallmentData(formatted);
    setPaidAmount(paid);
    setUnpaidAmount(unpaid);
    setTotalAmount(total);
    // keep appResults compatible with the single-app rendering path
    setAppResults([{
      appId: data.header?.data?.arrangementId,
      result: data.body || [],
      totalAmount: total,
      paidAmount: paid,
      unpaidAmount: unpaid,
    }]);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const user = getUser();
        if (!user) return;

        const isV2 = user?.upgrade === true;
        const res = await getInstlmntDashboardData({
          id: Number(user.IDB_USR_ID),
          ...(selectedApplId ? { applId: selectedApplId } : {}),
        }).unwrap();

        if (isV2) {
          // V2: Temenos response { opstatus, header, body, httpStatusCode }
          if (res.opstatus !== 0 || !res.body || res.body.length === 0) {
            toast.error(translate('SETTINGS.NO_SIGNED_APPLICATIONS_FOUND') || "No Signed Applications found");
            navigate(appRoute(routes.SETTING_ROOT));
            return;
          }
          processAppDataV2(res);
        } else {
          // V1: { success, result: [{ appId, totalAmount, paidAmount, unpaidAmount, result: [...] }] }
          if (!res.success) {
            throw new Error(res.message || "Failed to fetch installment dashboard data");
          }
          const result = res?.result || [];
          setAppResults(result);
          if (result.length === 1) {
            processAppData(result[0]);
          } else if (result.length > 1) {
            // Multiple apps: show selection cards
            setInstallmentData([]);
            setPaidAmount(0);
            setUnpaidAmount(0);
            setTotalAmount(0);
            setNextPaymentDate(null);
            setUpcomingAmount(0);
          } else {
            // No apps
            setInstallmentData([]);
            setPaidAmount(0);
            setUnpaidAmount(0);
            setTotalAmount(0);
            setNextPaymentDate(null);
            setUpcomingAmount(0);
          }
        }
      } catch (err) {
        console.error(err);
        toast.error(translate('SETTINGS.NO_SIGNED_APPLICATIONS_FOUND') || "No Signed Applications found");
        navigate(appRoute(routes.SETTING_ROOT));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedApplId]);

  // Helper to check if selected app (or only app) has no installment data
  const noInstallmentData =
    (appResults.length === 1 && appResults[0]?.result?.length === 0) ||
    (appResults.length > 1 && selectedAppId && appResults.find(a => a.appId === selectedAppId)?.result?.length === 0);

  return (
    <>
      {
        isLoading ? (
          <div className="flex justify-center items-center h-full">
            <Spin size="large" />
          </div>
        ) : appResults.length > 1 && !selectedAppId ? (
          <>
            <div className="gap-3 flex items-center mb-5">
              <div
                className="bg-[#F3EDDD] rounded-lg hover:bg-gray-100 transition duration-300 flex items-center justify-center w-10 h-10"
                onClick={navigateBack}
              >
                {isRtl ? (
                  <ArrowRightOutlined className="text-black text-lg font-extrabold" />
                ) : (
                  <ArrowLeftOutlined className="text-black text-lg font-extrabold" />
                )}
              </div>
              <div className="text-[#161616] text-[22px] font-normal font-sans">
                {translate("SETTINGS.SETTINGS")}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {appResults.map(app => {
                // Use pre-calculated amounts from API
                const paid = app.paidAmount || 0;
                const unpaid = app.unpaidAmount || 0;
                const total = app.totalAmount || 0;
                
                return (
                  <Card
                    key={app.appId}
                    hoverable
                    onClick={() => { setSelectedAppId(app.appId); processAppData(app); }}
                  >
                    <div className="mb-2 font-semibold">App #{app.appId}</div>
                    <div className="text-sm text-gray-600 mb-3">
                      <div>Total: {total.toLocaleString()} SAR</div>
                      <div>Paid: {paid.toLocaleString()} SAR</div>
                      <div>Unpaid: {unpaid.toLocaleString()} SAR</div>
                    </div>
                    <Chart
                      series={[paid, unpaid]}
                      options={{ labels: ['Paid', 'Pending'], colors: ['#22C55E', '#EF4444'], chart: { type: 'donut' } }}
                      type="donut"
                      width={200}
                      height={150}
                    />
                  </Card>
                );
              })}
            </div>
          </>
        ) : noInstallmentData ? (
          <div className="flex flex-col min-h-[350px] bg-white rounded-xl shadow p-8">
            {/* Back Button */}
            <div className="gap-3 flex items-center">
              <div
                className="bg-[#F3EDDD] rounded-lg hover:bg-gray-100 transition duration-300 flex items-center justify-center w-10 h-10"
                onClick={navigateBack}
              >
                {isRtl ? (
                  <ArrowRightOutlined className="text-black text-lg font-extrabold" />
                ) : (
                  <ArrowLeftOutlined className="text-black text-lg font-extrabold" />
                )}
              </div>
              <div className="text-[#161616] text-[22px] font-normal font-sans">
                {translate("SETTINGS.BACK")}
              </div>
            </div>
            {/* Empty State */}
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div className="flex flex-col items-center">
                  <span className="text-gray-700 text-lg font-semibold mb-2">
                    {translate("SETTINGS.INSTALLMENT_DATA_UNAVAILABLE") || "Installment data is unavailable"}
                  </span>
                  <span className="text-gray-500 text-base">
                    {translate("SETTINGS.INSTALLMENT_DATA_UNAVAILABLE_DESC") ||
                      "There are currently no installment records available for this application."}
                  </span>
                </div>
              }
            />
            <div className="mt-8 flex flex-col items-center">
              <svg width="64" height="64" fill="none" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="32" fill="#F3EDDD" />
                <path d="M32 20v12M32 44h.02M24 32a8 8 0 1 1 16 0 8 8 0 0 1-16 0Z" stroke="#D97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="text-[#D97706] font-medium mt-3">
                {translate("SETTINGS.CHECK_BACK_LATER") || "Please check back later or contact support if you believe this is an error."}
              </span>
            </div>
          </div>
        ) : (
          <>
            <div className="rounded-xl min-h-16 mb-4 px-4 py-8 bg-white items-center shadow-[0_4px_10px_-2px_rgba(0,0,0,0.1),0_-4px_10px_-2px_rgba(0,0,0,0.05)]">

              <div className="gap-3 flex items-center">
                <div
                  className="bg-[#F3EDDD] rounded-lg hover:bg-gray-100 transition duration-300 flex items-center justify-center w-10 h-10"
                  onClick={navigateBack}
                >
                  {isRtl ? (
                    <ArrowRightOutlined className="text-black text-lg font-extrabold" />
                  ) : (
                    <ArrowLeftOutlined className="text-black text-lg font-extrabold" />
                  )}
                </div>
                <div className="text-[#161616] text-[22px] font-normal font-sans">
                  {translate("SETTINGS.BACK")}
                </div>
              </div>



              {/* <div className="text-black text-2xl font-semibold mt-3 mb-3">
                {translate("SETTINGS.INSTLMNT_DASHBOADRD_SUMMARY")}
              </div> */}


              <div className="my-5 rounded-[20px] md:px-5">
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 mb-5">
                  <RepaymentCard 
                    title={translate('SETTINGS.CONTRACT_AMOUNT')} 
                    img={contractedAmount} 
                    description={totalAmount.toLocaleString()} 
                  />
                  <RepaymentCard 
                    title={translate('SETTINGS.REMAINING_AMOUNT')} 
                    img={remainingAmount} 
                    description={unpaidAmount.toLocaleString()} 
                  />
                  <RepaymentCard 
                    title={translate('SETTINGS.PAID_AMOUNT')} 
                    img={paidAmountImg} 
                    description={paidAmount.toLocaleString()} 
                  />
                  <RepaymentCard 
                    title={translate('SETTINGS.NEXT_PAYMENT_DUE')} 
                    img={nextPayment} 
                    description={nextPaymentDate ? formatDateDDMMYYYY(nextPaymentDate) : translate('SETTINGS.NO_UPCOMING_PAYMENTS') || 'N/A'} 
                  />
                </div>
                <div className="grid grid-cols-4 gap-8">
                  <div className="col-span-4 px-4 pt-8 lg:col-span-2 xl:col-span-3 h-fit bg-white shadow-[0_4px_39px_0_rgba(81,69,159,0.08)] rounded-[14px] order-1 lg:order-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">
                      {translate("SETTINGS.REPAYMENT_PROGRESS") || "Repayment Progress"}
                    </h3>
                    <ApexChart installmentData={installmentData} />
                  </div>
                  <div className="col-span-4 lg:col-span-2 xl:col-span-1 px-4 pt-8 pb-8 bg-white shadow-[0_4px_39px_0_rgba(81,69,159,0.08)] rounded-[14px] h-fit self-start hidden lg:block order-3 lg:order-2">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">
                      {translate("SETTINGS.NEXT_PAYMENT_DATE") || "Repayment Schedule"}
                    </h3>
                    <CalendarView
                      startDate={nextPaymentDate || new Date()} // highlighted day
                      specialDate1={installmentData?.find(i => i.status === 'Pending')?.rawDate} // one yellow dot
                      specialDate2={installmentData?.filter(i => i.status === 'Pending')?.[1]?.rawDate} // two dots
                    />
                  </div>

                  <div className="px-4 py-8 bg-white shadow-[0_4px_39px_0_rgba(81,69,159,0.08)] rounded-[14px] col-span-4 lg:col-span-2 xl:col-span-3 order-4 lg:order-3">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">
                      {translate("SETTINGS.REPAYMENT_STATUS") || "Repayment Schedule"}
                    </h3>

                    <div className="block overflow-x-auto ">
                      <table className="w-full border-collapse rounded-lg">
                        <thead>
                          <tr className="hover:bg-gray-50 border-b border-[#F1F1F1]">
                            <th className="px-2 md:px-4 py-2 md:py-3 text-start font-semibold text-gray-700 text-xs md:text-base">
                              {translate("SETTINGS.INSTALLMENT_NUMBER") || "Installment Number"}
                            </th>
                            <th className="px-2 md:px-4 py-2 md:py-3 text-start font-semibold text-gray-700 text-xs md:text-base">
                              {translate("SETTINGS.INSTALLMENT_DUE_DATE") || "Due Date"}
                            </th>
                            <th className="px-2 md:px-4 py-2 md:py-3 text-start font-semibold text-gray-700 text-xs md:text-base">
                              {translate("SETTINGS.INSTALLMENT_AMOUNT") || "Installment Amount"}
                            </th>
                            <th className="px-2 md:px-4 py-2 md:py-3 text-start font-semibold text-gray-700 text-xs md:text-base">
                              {translate("SETTINGS.INSTALLMENT_STATUS") || "Status"}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {installmentData?.slice(0, visibleInstallments).map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-2 md:px-4 py-2 md:py-3 text-gray-800 text-xs md:text-base">
                                {item.installmentNumber}
                              </td>
                              <td className="px-2 md:px-4 py-2 md:py-3 text-gray-800 text-xs md:text-base">
                                {item.dueDate}
                              </td>
                              <td className="px-2 md:px-4 py-2 md:py-3 text-gray-800 text-xs md:text-base">
                                {item.installmentAmount}
                              </td>
                              <td className="px-2 md:px-4 py-2 md:py-3">
                                <span
                                  className={`min-w-[40px] md:min-w-[100px] text-center inline-block px-1 py-1 md:py-2 text-[10px] md:text-xs font-semibold rounded-full ${item.status === 'Paid'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-[#FDECCE] text-[#F59E0B]'
                                    }`}
                                >
                                  {item.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {visibleInstallments < installmentData?.length && (
                        <div className="flex justify-center mt-4">
                          <button
                            onClick={handleShowMore}
                            className="px-4 py-1.5 md:px-6 md:py-2 text-sm bg-[#006E7E] text-white rounded-md hover:bg-[#005566] transition-colors"
                          >
                            {translate("SETTINGS.READ_MORE") || "Show More"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>


                  <div className="col-span-4 lg:col-span-2 xl:col-span-1 px-4 py-8 bg-white shadow-[0_4px_39px_0_rgba(81,69,159,0.08)] rounded-[14px] h-fit self-start order-2 lg:order-4">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">
                      {translate("SETTINGS.REPAYMENT_SCHEDULE") || "Repayment Schedule"}
                    </h3>
                    <div className="flex justify-center">
                      <StatusChart paid={paidAmount ? paidAmount : 0} unpaid={unpaidAmount ? unpaidAmount : 0} upcoming={upcomingAmount ? upcomingAmount : 0} />
                    </div>
                  </div>
                </div>

                {/* Cards for mobile screens */}
                <div className="hidden space-y-4">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">
                    {translate("SETTINGS.REPAYMENT_SCHEDULE") || "Repayment Schedule"}
                  </h3>
                  {installmentData.slice(0, visibleInstallments).map((item, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                      <div className="flex flex-col space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-900 font-semibold">
                            {translate("SETTINGS.INSTALLMENT_NUMBER") || "Installment Number"}:
                          </span>
                          <span className="text-gray-900 font-semibold">
                            {item.installmentNumber}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-600 text-sm">
                            {translate("SETTINGS.INSTALLMENT_DUE_DATE") || "Due Date"}:
                          </span>
                          <span className="text-gray-900 font-medium">
                            {item.dueDate}
                          </span>
                        </div>

                        <div className="flex flex-row justify-between items-center">
                          <div className="flex items-center">
                            <img
                              src={SaudiRiyal}
                              alt="Tamweel Logo"
                              className="h-5 w-5 mr-2"
                            />
                            <span className="text-gray-900 font-medium">
                              {item.installmentAmount.toLocaleString()}
                              <span className="text-gray-600 text-sm ml-1">amount</span>
                            </span>
                          </div>
                          <span
                            className={`px-3 py-1 text-xs font-medium rounded-md ${item.status === 'Paid'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                              }`}
                          >
                            {item.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {visibleInstallments < installmentData?.length && (
                    <div className="flex justify-center mt-4">
                      <button
                        onClick={handleShowMore}
                        className="px-4 py-1.5 md:px-6 md:py-2 text-sm bg-[#006E7E] text-white rounded-md hover:bg-[#005566] transition-colors"
                      >
                        {translate("SETTINGS.SHOW_MORE") || "Show More"}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2">
              </div>
            </div>
            <p className="text-center text-gray-500 text-sm mt-4">
              {translate("SETTINGS.ALL_RIGHTS_RESERVED") || "All Right Reserved"}
            </p>
          </>
        )}
    </>
  );
}

export default InstlmntDashboard;
